import { Link } from "react-router-dom";
import { useSeo } from "../../lib/seo";
import { GuideLayout, H2, P, Check } from "./GuideLayout";

const ROWS: Array<[string, string, string, string, string]> = [
  ["Finder kann Besitzer sofort erreichen", "nein", "ja, per Telefon", "nein", "ja, Standort + Nachricht"],
  ["Deine Telefonnummer bleibt privat", "ja", "nein", "ja", "ja"],
  ["Funktioniert ohne Akku", "ja", "ja", "nein (1 bis 5 Tage)", "ja"],
  ["Laufende Kosten", "keine", "keine", "Abo, meist 5 bis 10 € / Monat", "keine"],
  ["Gewicht am Tier", "keins", "gering", "20 bis 40 g, für Katzen oft zu viel", "wenige Gramm"],
  ["Anschaffung", "Tierarzt, ca. 30 bis 50 €", "5 bis 20 €", "50 bis 150 €", "10 bis 15 €"],
  ["Hilft, wenn das Tier gefunden wird", "nur beim Tierarzt / Tierheim", "ja", "ja", "ja"],
  ["Hilft, wenn das Tier nicht gefunden wird", "nein", "nein", "ja, Live-Standort", "nein"],
];

export function VergleichPage() {
  useSeo({
    title: "QR-Anhänger, Tasso-Chip oder GPS-Tracker für die Katze?",
    description:
      "Chip und Register, Adressmarke, GPS-Tracker oder QR-Anhänger: Was hilft wirklich, wenn die Katze wegläuft? Der ehrliche Vergleich mit Kosten, Gewicht, Akku und Datenschutz.",
    path: "/qr-anhaenger-vs-tasso-gps",
  });

  return (
    <GuideLayout
      eyebrow="Ratgeber · Vergleich"
      title="QR-Anhänger, Tasso-Chip oder GPS-Tracker: Was hilft wirklich?"
      intro="Es gibt vier Wege, ein entlaufenes Tier nach Hause zu bringen. Sie lösen unterschiedliche Probleme, und keiner ersetzt den anderen komplett. Hier ist der Vergleich ohne Verkaufsgerede."
    >
      <H2>Die vier Optionen in einem Satz</H2>
      <Check
        items={[
          <><strong>Chip plus Register (Tasso, Findefix):</strong> Die Katze ist eindeutig identifizierbar, aber nur mit einem Lesegerät beim Tierarzt oder im Tierheim. Der Finder muss sie einfangen und hinbringen.</>,
          <><strong>Adressmarke am Halsband:</strong> Deine Nummer steht öffentlich am Tier. Funktioniert, solange die Gravur lesbar ist und der Finder anrufen mag.</>,
          <><strong>GPS-Tracker:</strong> Du siehst den Standort live, solange der Akku hält und Mobilfunk da ist. Für Katzen oft zu schwer, dazu ein Abo.</>,
          <><strong>QR-Anhänger (petloc):</strong> Der Finder scannt mit jedem Handy und schickt dir Standort und Nachricht, ohne deine Nummer zu sehen. Kein Akku, kein Abo.</>,
        ]}
      />

      <H2>Der direkte Vergleich</H2>
      <div className="overflow-x-auto -mx-6 px-6 mb-6">
        <table className="w-full min-w-[720px] text-[14.5px] border-separate border-spacing-0">
          <thead>
            <tr className="text-left text-[#6E5E66]">
              <th className="py-2 pr-3 font-medium"></th>
              <th className="py-2 px-3 font-semibold text-[#211019]">Chip + Register</th>
              <th className="py-2 px-3 font-semibold text-[#211019]">Adressmarke</th>
              <th className="py-2 px-3 font-semibold text-[#211019]">GPS-Tracker</th>
              <th className="py-2 px-3 font-semibold text-brand-800 bg-[#FBEEFB] rounded-t-xl">QR-Anhänger</th>
            </tr>
          </thead>
          <tbody>
            {ROWS.map(([label, a, b, c, d], i) => (
              <tr key={i} className="align-top">
                <td className="py-2.5 pr-3 border-t border-[#ECE3E7] text-[#3E2F37]">{label}</td>
                <td className="py-2.5 px-3 border-t border-[#ECE3E7]">{a}</td>
                <td className="py-2.5 px-3 border-t border-[#ECE3E7]">{b}</td>
                <td className="py-2.5 px-3 border-t border-[#ECE3E7]">{c}</td>
                <td className="py-2.5 px-3 border-t border-[#F3D7F2] bg-[#FBEEFB] font-medium text-[#2E2027]">{d}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <H2>Was ist mit AirTags?</H2>
      <P>
        Ein AirTag hat kein GPS. Er wird nur gefunden, wenn ein fremdes iPhone in Bluetooth-Reichweite vorbeikommt. In der Stadt klappt das manchmal, am Feldrand nicht. Apple selbst empfiehlt AirTags nicht zum Orten von Haustieren. Als Ergänzung in dicht besiedelten Gegenden brauchbar, als einziger Schutz nicht.
      </P>

      <H2>Warum der Chip allein nicht reicht</H2>
      <P>
        Der Chip ist Pflichtprogramm und in vielen Regionen sogar vorgeschrieben. Aber er hat eine Lücke: Er sagt dem Finder nichts. Wer eine fremde Katze im Garten sieht, hat kein Lesegerät und wird sie selten einfangen und zum Tierarzt fahren. Die Katze wird gefüttert, bleibt in der Nachbarschaft, und niemand weiß, dass sie vermisst wird. Ein sichtbarer Kontaktweg am Halsband schließt genau diese Lücke.
      </P>

      <H2>Wann sich ein GPS-Tracker lohnt</H2>
      <P>
        Für Hunde, die jagen oder ausreißen, und für Katzen in unübersichtlichem Gelände kann ein Tracker sinnvoll sein, weil er auch dann hilft, wenn niemand das Tier findet. Der Preis dafür: tägliches Laden, ein Abo und ein Gewicht, das kleine Katzen dauerhaft stört. Viele Halter legen ihn nach ein paar Wochen in die Schublade. Ein QR-Anhänger dagegen ist immer dran.
      </P>

      <H2>Unsere Empfehlung</H2>
      <Check
        items={[
          <><strong>Immer:</strong> Chip plus aktuelle Registrierung.</>,
          <><strong>Dazu:</strong> QR-Sicherheitshalsband oder Mini-Anhänger am vorhandenen Halsband, damit der Finder dich sofort erreicht. Mit Sicherheitsverschluss, der sich löst, wenn die Katze hängen bleibt.</>,
          <><strong>Optional:</strong> GPS-Tracker für Hunde mit Jagdtrieb oder Katzen mit hohem Risiko.</>,
        ]}
      />
      <P>
        Was im Ernstfall konkret zu tun ist, steht in der <Link to="/katze-vermisst" className="text-brand-600 font-semibold">Checkliste „Katze vermisst“</Link>.
      </P>
    </GuideLayout>
  );
}
